import {
  bodyFatPercentNavy,
  leanMassKg,
  type Measurement,
  type Sex,
} from "@jackedlogs/shared";

// Body-composition trend series (body-fat % + lean mass) from a sparse
// measurement history — the math behind the Composition chart.
//
// Model C makes entries SPARSE: a tape day may carry only the waist (the
// thing that moved), a weigh-in only the weight. The US Navy body-fat
// formula needs height + neck + waist (+ hips for females) TOGETHER, so to
// keep BF% tracking every change WITHOUT persisting derived values
// (the raw inputs are already the history — feedback_no_denormalized_display_snapshots),
// we carry each input forward PER FIELD (like the per-field "current"
// resolution in latestValues.ts). A tape day that updates only the waist
// recomputes BF% against the last-known neck/hips — so the readout no
// longer freezes on a partial tape day (the bug Antenore reported
// 2026-06-23: "body fat fermo nonostante le misure siano cambiate").
//
// BF% plots only on dates that actually carry a tape input — a weight-only
// weigh-in adds no BF% point (no clutter) but still moves lean mass
// (= weight × (1 − BF%)) using the last-known BF%.
//
// Lifted to @jackedlogs/ui (S3, 2026-06-29) with MeasurementTrends — pure
// math, no platform deps, so the web console renders the same curves.

export type CompositionPoint = { takenAt: string; value: number };
export type CompositionSeries = {
  fat: CompositionPoint[];
  lean: CompositionPoint[];
};

// `chrono` MUST be oldest→newest (the carry-forward walks time forward).
export function compositionSeries(
  chrono: Measurement[],
  sex: Sex | undefined,
): CompositionSeries {
  const fat: CompositionPoint[] = [];
  const lean: CompositionPoint[] = [];
  let runHeight: number | undefined;
  let runNeck: number | undefined;
  let runWaist: number | undefined;
  let runHips: number | undefined;
  let runBf: number | null = null;

  for (const m of chrono) {
    if (typeof m.heightCm === "number") runHeight = m.heightCm;
    if (typeof m.neckCm === "number") runNeck = m.neckCm;
    if (typeof m.waistCm === "number") runWaist = m.waistCm;
    if (typeof m.hipsCm === "number") runHips = m.hipsCm;

    // A tape input is neck/waist/hips (height is excluded: it's effectively
    // constant, measured once, so a height-only day shouldn't plot a BF%
    // point). BF% recomputes whenever one of these moves.
    const carriesTape =
      typeof m.neckCm === "number" ||
      typeof m.waistCm === "number" ||
      typeof m.hipsCm === "number";

    if (sex && carriesTape) {
      const bf = bodyFatPercentNavy({
        sex,
        heightCm: runHeight ?? 0,
        neckCm: runNeck ?? 0,
        waistCm: runWaist ?? 0,
        hipsCm: runHips,
      });
      if (bf !== null) {
        fat.push({ takenAt: m.takenAt, value: bf });
        runBf = bf;
      }
    }

    if (m.weightKg !== undefined && runBf !== null) {
      const lm = leanMassKg(m.weightKg, runBf);
      if (lm !== null) lean.push({ takenAt: m.takenAt, value: lm });
    }
  }

  return { fat, lean };
}
